import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Breadcrumb — semantic HTML + JSON-LD structured data for SEO
 * Props:
 *   items: [{ label, path }]  — last item has no path (current page)
 *   color: accent color for last crumb
 */
export default function Breadcrumb({ items, color = '#10B981' }) {
  const navigate = useNavigate();

  // Inject JSON-LD BreadcrumbList for SEO
  useEffect(() => {
    const id = 'breadcrumb-jsonld';
    const existing = document.getElementById(id);
    if (existing) existing.remove();

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: item.label,
        ...(item.path ? { item: `https://revupai.com${item.path}` } : {}),
      })),
    };

    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => { const el = document.getElementById(id); if (el) el.remove(); };
  }, [items]);

  return (
    <nav aria-label="Breadcrumb" style={{ marginBottom: 20 }}>
      <ol style={{
        listStyle: 'none', margin: 0, padding: 0,
        display: 'flex', alignItems: 'center', gap: 6,
        flexWrap: 'wrap',
      }}>
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          return (
            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {i > 0 && (
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                  stroke="rgba(200,192,236,0.3)" strokeWidth="2.5" aria-hidden="true">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              )}
              {isLast ? (
                <span style={{
                  fontFamily: "'DM Sans',system-ui,sans-serif",
                  fontSize: 12, fontWeight: 600,
                  color: color,
                }} aria-current="page">{item.label}</span>
              ) : (
                <button
                  onClick={() => item.path && navigate(item.path)}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    fontFamily: "'DM Sans',system-ui,sans-serif",
                    fontSize: 12, fontWeight: 400,
                    color: 'rgba(200,192,236,0.5)',
                    transition: 'color .15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = 'rgba(200,192,236,0.85)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(200,192,236,0.5)'}
                >{item.label}</button>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}